import React from 'react';
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import type { BtbBreakdownPublic, BtbPlanningBaselinePublic, BtbTechnologyMoneyRangePublic, CostDataSummary, CostDataPrimaryStatus, EstimateCapabilities, EstimateRevision, GeorgiaObservedEvidencePublic } from '../api/constructionPriceEstimatorFunctions';
import { formatConstructionMoney } from './constructionPriceMoney';
import ConstructionPriceLegacyEvidence from './ConstructionPriceLegacyEvidence';
import { OBSOLETE_MATERIAL_OBSERVATION_WARNING } from '../api/approximateCostPlan';

export const money = formatConstructionMoney;

// BTB occasionally uses a currency-year token (for example `2023$`) in the
// source's unit column. Dollar amounts already carry the currency symbol, so
// render that token as `$`; source-year information remains in its separate
// year label rather than masquerading as a unit.
const displayBtbUnitLabel = (value: string): string => {
  const label = value.trim();
  return label.includes('$') && /^\$?\s*20\d{2}\s*\$?$/.test(label) ? '$' : label;
};

export const date = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return 'Not available';
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(parsed);
};

export const textList = (value: unknown): string[] => Array.isArray(value)
  ? value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
  : [];

// The estimator API intentionally keeps machine-readable enums in its saved
// records.  Never let those storage/API values become UI copy: use an explicit
// customer-facing label where one is useful, otherwise omit it.
const BACKEND_VALUE_LABELS: Record<string, string> = {
  manual_allowance: 'User allowance',
  user_allowance: 'User allowance',
  quartile_25_median_75: '25th percentile, median, and 75th percentile',
  minimum_median_maximum: 'Minimum, median, and maximum',
  square_foot: 'square foot',
  linear_foot: 'linear foot',
  cubic_yard: 'cubic yard',
  lump_sum: 'lump sum',
  ROUND_HALF_UP: 'round half up',
  largest_remainder_stable_order: 'largest remainder allocation',
  not_applicable: 'Not applicable',
  not_requested: 'Not requested',
  user_entered: 'User entered',
  reviewer_excluded: 'Excluded during review',
};

const OMITTED_PRICE_DISCLOSURES = new Set([
  OBSOLETE_MATERIAL_OBSERVATION_WARNING,
  'Historical award totals are not itemized unit pricing, an itemized estimate, or an IGE. This low-confidence planning range requires Government review.',
  'Historical dollars — no BLS adjustment applied. Award totals are source-date evidence, not estimate-date dollars, unit prices, or an IGE.',
]);

const isBackendIdentifier = (value: string): boolean => /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(value);

export const displayBackendValue = (value: unknown, fallback = 'Not reported'): string => {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string' || !value.trim()) return fallback;
  const trimmed = value.trim();
  return BACKEND_VALUE_LABELS[trimmed] ?? (isBackendIdentifier(trimmed) ? fallback : trimmed);
};

export const displayUserMessages = (value: unknown): string[] => textList(value)
  .filter((message) => !OMITTED_PRICE_DISCLOSURES.has(message))
  .map((message) => displayBackendValue(message, ''))
  .filter(Boolean);

export const blockerText = (blocker: string | { message?: string; code?: string }): string => (
  typeof blocker === 'string'
    ? displayBackendValue(blocker, 'Additional project information is required.')
    : displayBackendValue(blocker.message ?? blocker.code, 'Additional project information is required.')
);

export const sourceLabel = (source: unknown): string => {
  const normalized = typeof source === 'string' ? source : '';
  const labels: Record<string, string> = {
    licensed_provider: 'Licensed construction cost provider',
    manual_allowance: 'User allowance',
    user_allowance: 'User allowance',
    bls: 'BLS escalation',
    BLS: 'BLS escalation',
    usaspending: 'USAspending comparables',
    USAspending: 'USAspending comparables',
    usaspending_historical_awards: 'USAspending comparables',
    sam_contract_awards: 'SAM Contract Awards',
    sam_opportunities: 'SAM Opportunities',
    davis_bacon: 'Wage determination',
    odot_bid_tabs: 'ODOT bid tabs',
    calculation: 'Deterministic calculation',
  };
  return labels[normalized] ?? displayBackendValue(normalized, 'Source');
};

export const costDataPrimaryStatus = (capabilities: EstimateCapabilities | undefined): CostDataPrimaryStatus => {
  const value = capabilities?.cost_data_primary;
  return value && typeof value === 'object' ? value.status : 'not_configured';
};

export const costDataPrimaryConfigured = (status: CostDataPrimaryStatus): boolean => (
  status === 'configured' || status === 'eligible'
);

const COST_DATA_AVAILABILITY_MESSAGES: Partial<Record<CostDataPrimaryStatus, string>> = {
  rights_pending: 'Item-level cost data is awaiting source-rights approval. Previous federal awards remain separate research context.',
  rights_restricted: 'Item-level cost data is not authorized for this estimate. Previous federal awards remain separate research context.',
  source_unavailable: 'Approved item-level cost data is temporarily unavailable. Previous federal awards remain separate research context.',
  no_eligible_coverage: 'No eligible item-level cost evidence covers these contract details. Previous federal awards remain separate research context.',
};

export const costDataAvailabilityMessage = (status: CostDataPrimaryStatus): string | null => (
  COST_DATA_AVAILABILITY_MESSAGES[status] ?? null
);

const unresolvedAction = (): string => 'Add a user allowance or exclude this item from the estimate.';

export const CostDataCoverage: React.FC<{ summary: CostDataSummary }> = ({ summary }) => {
  const provenance = Array.isArray(summary.provenance) ? summary.provenance : [];
  const isNoCoverage = summary.decision === 'insufficient_item_evidence' || summary.decision === 'award_research_only';
  const coverageMessage = isNoCoverage
    ? 'No published Texas heavy-civil installed-price evidence matches this item and unit yet.'
    : `${summary.covered_item_count} of ${summary.included_item_count} included item${summary.included_item_count === 1 ? '' : 's'} have eligible item-level cost evidence.`;
  const freshness = summary.source_freshness === 'current'
    ? 'Current approved source snapshot'
    : summary.source_freshness === 'stale'
      ? 'An earlier approved source snapshot was used'
      : null;
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5, mb: 2, borderLeft: 4, borderLeftColor: 'success.main' }}>
      <Typography variant="h6" component="h2">Cost-data coverage</Typography>
      <Typography sx={{ mt: 0.5 }}>{coverageMessage}</Typography>
      {provenance.map((item, index) => (
        <Typography key={`${item.source_label}-${index}`} variant="body2" color="text.secondary" sx={{ mt: index === 0 ? 0.5 : 0 }}>
          {displayBackendValue(item.source_label, 'Approved cost-data source')}
        </Typography>
      ))}
      {freshness && <Typography variant="body2" color="text.secondary">{freshness}</Typography>}
      {summary.unresolved_items.length > 0 && (
        <Box component="ul" sx={{ mb: 0.25, mt: 1, pl: 2.5 }}>
          {summary.unresolved_items.map((item, index) => <li key={`${item.work_item_id ?? item.label}-${index}`}>
            <Typography component="span" fontWeight={700}>{item.label}</Typography>
            <Typography component="span"> — {unresolvedAction()}</Typography>
          </li>)}
        </Box>
      )}
      {isNoCoverage && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Previous federal awards remain separate research context.</Typography>}
    </Paper>
  );
};

/**
 * Snapshot-backed installed work is deliberately presented apart from the
 * component table: a bid-tab unit rate is an all-in observation, not a browser
 * derivation of labor, materials, equipment, or subcontract values.
 */
export const InstalledPriceEvidence: React.FC<{ revision: EstimateRevision }> = ({ revision }) => {
  const installedItems = (revision.work_items ?? []).filter((item) => (
    item.cost_data?.pricing_kind === 'installed_work'
    && item.cost_data.unit_range
    && item.cost_data.total_range
    && typeof item.unit === 'string'
    && item.unit.trim().length > 0
  ));
  if (installedItems.length === 0) return null;

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="h5" component="h2">Texas heavy-civil installed price evidence</Typography>
      {installedItems.map((item) => {
        const unitRange = item.cost_data!.unit_range!;
        const totalRange = item.cost_data!.total_range!;
        const provenance = item.cost_data?.provenance;
        const adjustment = provenance?.time_adjustment;
        return (
          <Box key={String(item.id ?? item.work_item_id)} sx={{ mt: 1.25 }}>
            <Typography fontWeight={700}>{item.description}</Typography>
            <Typography variant="body2">
              {money(unitRange.low, revision.currency)} / {money(unitRange.base, revision.currency)} / {money(unitRange.high, revision.currency)} per {displayBackendValue(item.unit, 'unit')} (low / median / high)
            </Typography>
            <Typography variant="body2">
              {money(totalRange.low, revision.currency)} / {money(totalRange.base, revision.currency)} / {money(totalRange.high, revision.currency)} total (low / median / high)
            </Typography>
            <Typography variant="body2" color="text.secondary">Installed bid-tab prices; components not separately reported</Typography>
            {provenance?.source_label && <Typography variant="body2" color="text.secondary">{provenance.source_label}</Typography>}
            {provenance?.effective_date && <Typography variant="body2" color="text.secondary">Source effective date: {date(provenance.effective_date)}</Typography>}
            {provenance?.geography_fit && <Typography variant="body2" color="text.secondary">Geography: {provenance.geography_fit === 'county' ? 'Texas county' : 'Texas statewide'}</Typography>}
            <Typography variant="body2" color="text.secondary">Pricing date: {date(revision.estimate_date)}</Typography>
            {provenance?.selected_observation_count && <Typography variant="body2" color="text.secondary">
              {provenance.selected_observation_count} independent project observation{provenance.selected_observation_count === 1 ? '' : 's'}
            </Typography>}
            {provenance?.observation_start_date && provenance.observation_end_date && <Typography variant="body2" color="text.secondary">
              Observation dates: {date(provenance.observation_start_date)} to {date(provenance.observation_end_date)}
            </Typography>}
            {provenance?.snapshot_published_at && <Typography variant="body2" color="text.secondary">Published snapshot: {date(provenance.snapshot_published_at)}</Typography>}
            {adjustment?.status === 'adjusted' && <Typography variant="body2" color="text.secondary">
              {adjustment.source_label}: adjusted from {adjustment.observation_period} to {adjustment.reference_period}
            </Typography>}
          </Box>
        );
      })}
    </Paper>
  );
};

/**
 * BTB stays outside the award/estimate presentation. The server can omit the
 * entire public projection until display is separately authorized; the UI
 * does not describe hidden selection, policy, or source-run states.
 */
export const BtbPlanningBaseline: React.FC<{ baseline: BtbPlanningBaselinePublic }> = ({ baseline }) => (
  <Paper
    component="section"
    role="region"
    aria-labelledby="btb-planning-baseline-heading"
    variant="outlined"
    sx={{ p: 2.5, mb: 2, borderLeft: 4, borderLeftColor: 'info.main' }}
  >
    <Typography id="btb-planning-baseline-heading" variant="h5" component="h2">National planning baseline</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {baseline.evidence_type}
    </Typography>
    <Typography variant="h6" component="h3" sx={{ mt: 1.5 }}>{baseline.technology_label}</Typography>
    <Typography sx={{ mt: 0.5 }}>{money(baseline.value.amount, baseline.value.currency)} {displayBtbUnitLabel(baseline.value.unit_label)}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {baseline.geography_label} · {baseline.building_segment === 'commercial' ? 'Commercial' : 'Residential'} · {baseline.cost_basis_label} · {baseline.value_year_label}
    </Typography>
    {baseline.scenario_label && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{baseline.scenario_label}</Typography>}
    <Typography variant="body2" sx={{ mt: 1.5 }}>{baseline.limitations[0]}</Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{baseline.source_label}</Typography>
    <Typography variant="caption" color="text.secondary" display="block">{baseline.attribution}</Typography>
  </Paper>
);

const BtbTechnologyRange: React.FC<{ label: string; value: BtbTechnologyMoneyRangePublic }> = ({ label, value }) => (
  <Box sx={{ mt: 1.25 }}>
    <Typography variant="body2" fontWeight={700}>{label}</Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {money(value.low)} / {money(value.median)} / {money(value.high)} {displayBtbUnitLabel(value.unit_label)} (low / median / high)
    </Typography>
  </Box>
);

/**
 * The server supplies every displayed BTB value. This deliberately contains
 * no arithmetic: BTB detail is national technology evidence, not an itemized
 * decomposition of the award-derived project recommendation.
 */
export const BtbTechnologyBreakdown: React.FC<{
  breakdown: BtbBreakdownPublic;
}> = ({ breakdown }) => (
  <Box
    component="section"
    role="region"
    aria-label="Technology cost details"
  >
    <Typography variant="h6" component="h3">{breakdown.heading}</Typography>
    <Typography variant="body2" color="text.secondary">National benchmark · 2023 dollars</Typography>
      {breakdown.status === 'no_matches' && (
        <Typography sx={{ mt: 1 }}>No supported building-technology costs were found for this scope.</Typography>
      )}
      {breakdown.status === 'unavailable' && (
        <Typography sx={{ mt: 1 }}>National technology cost details are temporarily unavailable. Previous federal awards remain available.</Typography>
      )}
      {breakdown.items.map((item, index) => (
        <Paper key={`${item.project_item_label}-${item.technology_label}-${index}`} variant="outlined" sx={{ mt: 1.5, p: 2 }}>
          <Typography fontWeight={800}>{item.project_item_label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{item.technology_label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[item.building_segment, item.end_use_label, item.fuel_label].filter(Boolean).join(' · ')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Model year {item.model_year} · {item.scenario_label}{item.construction_scope_label ? ` · ${item.construction_scope_label}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.value_basis_label}{item.value_basis_label === 'Source-typical' && item.typical_unit_label ? ` · Typical ${item.typical_unit_label}` : ''}
          </Typography>
          <BtbTechnologyRange label="Equipment/retail benchmark" value={item.retail_benchmark} />
          <BtbTechnologyRange label="Installation allowance" value={item.installation_allowance} />
          {item.installed_total && <BtbTechnologyRange label="Installed technology total" value={item.installed_total} />}
          {item.installed_new_construction && <BtbTechnologyRange label="New-construction installed technology total" value={item.installed_new_construction} />}
          {item.installed_retrofit && <BtbTechnologyRange label="Retrofit installed technology total" value={item.installed_retrofit} />}
          {!item.installed_total && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Choose project scope to select one installed range.</Typography>}
          {item.range_quality_warning && <Alert severity="warning" sx={{ mt: 1.5 }}>{item.range_quality_warning}</Alert>}
          {item.negative_installation_warning && <Alert severity="info" sx={{ mt: 1.5 }}>{item.negative_installation_warning}</Alert>}
          {item.source_quality_summary && <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{item.source_quality_summary}</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{item.limitation}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{item.attribution}</Typography>
        </Paper>
      ))}
      {breakdown.unmatched_count !== undefined && breakdown.unmatched_count > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {breakdown.unmatched_count} additional scope item{breakdown.unmatched_count === 1 ? '' : 's'} had no BTB match.
        </Typography>
      )}
  </Box>
);

const GEORGIA_OBSERVED_EVIDENCE_COPY: Record<Exclude<GeorgiaObservedEvidencePublic['status'], 'available' | 'not_configured'>, string> = {
  no_match: 'No matching Georgia price records yet.',
  insufficient_evidence: 'More verified price records are needed.',
};

/**
 * This is intentionally supporting detail, not a second recommendation or
 * price range. Every amount and label arrives in the strict public DTO; JSX
 * does not calculate, classify, or decompose any observed installed line.
 */
export const GeorgiaObservedEvidence: React.FC<{
  evidence: GeorgiaObservedEvidencePublic;
}> = ({ evidence }) => (
  <Box>
    <Typography variant="h6" component="h3">{evidence.heading}</Typography>
    <Typography variant="body2" color="text.secondary">Observed all-in installed line prices from approved Georgia public-owner records</Typography>
      {evidence.status === 'available' ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            These are supporting observed lines, not a replacement for the planning price. Compare scope, quantities, and inclusions before relying on them.
          </Typography>
          {evidence.as_of && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Evidence current as of {date(evidence.as_of)}.</Typography>}
          <TableContainer sx={{ overflowX: 'auto', mt: 1.5 }}>
            <Table size="small" aria-label="Georgia observed installed line prices" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Work item</TableCell>
                  <TableCell>Observed description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Observed unit price</TableCell>
                  <TableCell align="right">Observed line price</TableCell>
                  <TableCell>Price date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evidence.items.map((item, index) => (
                  <TableRow key={`${item.work_item}-${item.source_reference}-${index}`}>
                    <TableCell component="th" scope="row">{item.work_item}</TableCell>
                    <TableCell>{item.original_description}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell align="right">{money(item.unit_price, item.currency)}</TableCell>
                    <TableCell align="right">{money(item.extended_price, item.currency)}</TableCell>
                    <TableCell>{date(item.price_date)}</TableCell>
                    <TableCell>{item.project_location}</TableCell>
                    <TableCell>{item.evidence_status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {evidence.items.map((item, index) => (
            <Box key={`${item.source_reference}-${index}`} sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Source: {item.source_label} · {item.source_reference}</Typography>
              {item.caveats.map((caveat) => <Typography key={caveat} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{caveat}</Typography>)}
            </Box>
          ))}
        </>
      ) : (
        <Typography sx={{ mt: 1 }}>{evidence.status === 'no_match'
          ? GEORGIA_OBSERVED_EVIDENCE_COPY.no_match
          : GEORGIA_OBSERVED_EVIDENCE_COPY.insufficient_evidence}</Typography>
      )}
      {evidence.warnings.filter(warning => warning !== OBSOLETE_MATERIAL_OBSERVATION_WARNING)
        .map((warning) => <Alert key={warning} severity="info" sx={{ mt: 1.5 }}>{warning}</Alert>)}
  </Box>
);

export function constructionSupportingEvidence({
  revision, summary, baseline, breakdown, georgia, state,
}: {
  revision: EstimateRevision | null;
  summary: CostDataSummary | null;
  baseline: BtbPlanningBaselinePublic | null;
  breakdown: BtbBreakdownPublic | null;
  georgia: GeorgiaObservedEvidencePublic | null;
  state: string | undefined;
}): Array<{ id: string; label: string; content: React.ReactNode }> {
  const evidence: Array<{ id: string; label: string; content: React.ReactNode }> = [];
  if (revision && (revision.work_items?.length || revision.adjustments?.length
    || revision.calculation || revision.totals)) {
    evidence.push({ id: 'saved-revision', label: 'Saved revision evidence',
      content: <ConstructionPriceLegacyEvidence revision={revision} /> });
  }
  if (summary) evidence.push({ id: 'cost-data-coverage', label: 'Cost-data coverage',
    content: <CostDataCoverage summary={summary} /> });
  if (revision?.work_items?.some((item) => item.cost_data?.pricing_kind === 'installed_work')) {
    evidence.push({ id: 'installed-price', label: 'Texas heavy-civil installed price evidence',
      content: <InstalledPriceEvidence revision={revision} /> });
  }
  if (baseline) evidence.push({ id: 'btb-baseline', label: 'National planning baseline',
    content: <BtbPlanningBaseline baseline={baseline} /> });
  if (breakdown) evidence.push({ id: 'btb-breakdown', label: 'Technology cost details',
    content: <BtbTechnologyBreakdown breakdown={breakdown} /> });
  if (georgia && state === 'GA' && georgia.status !== 'not_configured') {
    evidence.push({ id: 'georgia-observed', label: 'Georgia public-owner price evidence',
      content: <GeorgiaObservedEvidence evidence={georgia} /> });
  }
  return evidence;
}
